import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import useValueLabels from '../hooks/useValueLabels';
import { FileVisibilityBadge, BulkVisibilityButton, RoleFilter } from './FileVisibility';
import { fmtDateTimeWeek } from '../lib/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const download = async (f, t) => {
  try {
    const r = await axios.get(`${API}/files/attachment/${f.id}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = f.original_filename;
    a.click();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(e.response?.status === 410 ? t('auto.misc.fileIsNoLongerAvailable') : t('common.errorGeneric'));
  }
};

const Filters = ({ f, setF, categories, roleFilter, setRoleFilter }) => {
  const { t } = useTranslation();
  const vl = useValueLabels();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input className="h-8 w-56 text-xs" placeholder={t('fileVisibility.searchPlaceholder')} value={f.q}
        onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="admin-files-search" />
      <Select value={f.entity_type} onValueChange={(v) => setF({ ...f, entity_type: v })}>
        <SelectTrigger className="h-8 w-40 text-xs" data-testid="admin-files-entity-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('fileVisibility.allTypes')}</SelectItem>
          {['project', 'customer', 'employee'].map((e) => <SelectItem key={e} value={e}>{t(`fileVisibility.${e}`)}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
        <SelectTrigger className="h-8 w-44 text-xs" data-testid="admin-files-category-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('fileVisibility.allCategories')}</SelectItem>
          {categories.map((c) => <SelectItem key={c} value={c}>{vl(c)}</SelectItem>)}
        </SelectContent>
      </Select>
      <RoleFilter value={roleFilter} onChange={setRoleFilter} testId="admin-files-role-filter" />
    </div>
  );
};

const FileRow = ({ file, checked, onCheck, onSave }) => {
  const { t } = useTranslation();
  const vl = useValueLabels();
  return (
    <tr className="border-t hover:bg-slate-50" data-testid={`admin-file-row-${file.id}`}>
      <td className="p-2"><Checkbox checked={checked} onCheckedChange={(on) => onCheck(!!on)} data-testid={`admin-file-select-${file.id}`} /></td>
      <td className="p-2 max-w-[260px] truncate" title={file.original_filename}>{file.original_filename}</td>
      <td className="p-2 text-gray-600">
        <span className="text-[10px] uppercase tracking-wide text-gray-400 mr-1">{t(`fileVisibility.${file.entity_type}`)}</span>
        {file.entity_name || <span className="italic text-gray-400">{t('fileVisibility.deletedRecord')}</span>}
      </td>
      <td className="p-2">{file.category ? vl(file.category) : t('fileVisibility.noCategory')}</td>
      <td className="p-2"><FileVisibilityBadge attachment={file} onSave={onSave} /></td>
      <td className="p-2 text-gray-500 whitespace-nowrap">{file.uploaded_at ? fmtDateTimeWeek(file.uploaded_at) : ''}</td>
      <td className="p-2">
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => download(file, t)} data-testid={`admin-file-download-${file.id}`}>
          <Download className="h-3.5 w-3.5" />
        </Button>
      </td>
    </tr>
  );
};

const Pager = ({ page, pageSize, total, setPage, setPageSize }) => {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total ? (page - 1) * pageSize + 1 : 0;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600" data-testid="admin-files-pager">
      <div className="flex items-center gap-2">
        {t('fileVisibility.perPage')}
        <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
          <SelectTrigger className="h-8 w-20 text-xs" data-testid="admin-files-page-size"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[25, 50, 100].map((n) => <SelectItem key={n} value={String(n)} data-testid={`admin-files-page-size-${n}`}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <span data-testid="admin-files-range">{t('fileVisibility.range', { from, to: Math.min(page * pageSize, total), total })}</span>
        <Button size="sm" variant="outline" className="h-8 w-8 p-0" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-files-prev">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span data-testid="admin-files-page">{t('fileVisibility.pageOf', { page, pages })}</span>
        <Button size="sm" variant="outline" className="h-8 w-8 p-0" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="admin-files-next">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const BulkDownloadButton = ({ fileIds }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/attachments/download-zip`, { file_ids: fileIds }, { responseType: 'blob' });
      const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'files.zip';
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement('a'), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t('fileVisibility.zipReady', { count: fileIds.length }));
    } catch (e) {
      toast.error(t('fileVisibility.zipFailed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!fileIds.length || busy} onClick={go} data-testid="admin-files-download-zip">
      <Download className="h-3.5 w-3.5 mr-1" />{busy ? t('fileVisibility.zipBuilding') : t('fileVisibility.zipButton', { count: fileIds.length })}
    </Button>
  );
};

/** Admin: every attachment in one place, with role filter and bulk visibility. */
export const AdminFilesPanel = () => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [f, setF] = useState({ q: '', entity_type: 'all', category: 'all' });
  const [roleFilter, setRoleFilter] = useState({ on: false, roles: [] });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  useEffect(() => { setPage(1); }, [f, roleFilter, pageSize]);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setCategories(r.data?.attachment_categories || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const params = { page, page_size: pageSize };
    if (f.q.trim()) params.q = f.q.trim();
    if (f.entity_type !== 'all') params.entity_type = f.entity_type;
    if (f.category !== 'all') params.category = f.category;
    if (roleFilter.on) params.restricted = true;
    if (roleFilter.on && roleFilter.roles.length) params.roles = roleFilter.roles.join(',');
    try {
      const r = await axios.get(`${API}/admin/attachments`, { params });
      setFiles(r.data.items);
      setTotal(r.data.total);
      if (r.data.page !== page) setPage(r.data.page);
      setSelected([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.errorGeneric'));
    }
  }, [f, roleFilter, page, pageSize, t]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  const saveOne = async (id, v) => {
    try {
      await axios.patch(`${API}/attachment/${id}`, { visible_to_all: v.all, visible_roles: v.all ? [] : v.roles });
      toast.success(t('fileVisibility.saved'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('fileVisibility.saveFailed'));
    }
  };

  const allChecked = files.length > 0 && selected.length === files.length;
  return (
    <Card data-testid="admin-files-panel">
      <CardHeader>
        <CardTitle className="text-base">{t('fileVisibility.adminFilesTitle')}</CardTitle>
        <CardDescription>{t('fileVisibility.adminFilesDesc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Filters f={f} setF={setF} categories={categories} roleFilter={roleFilter} setRoleFilter={setRoleFilter} />
        <div className="flex items-center gap-3">
          <BulkDownloadButton fileIds={selected} />
          <BulkVisibilityButton fileIds={selected} onDone={load} testId="admin-files-bulk-visibility"
            legacyCount={files.filter((x) => selected.includes(x.id) && x.visible_to_all === undefined).length} />
          <span className="text-xs text-gray-500" data-testid="admin-files-count">
            {t('fileVisibility.selected', { count: selected.length })} · {total}
          </span>
        </div>
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left text-gray-600">
              <tr>
                <th className="p-2 w-8"><Checkbox checked={allChecked} onCheckedChange={(on) => setSelected(on ? files.map((x) => x.id) : [])} data-testid="admin-files-select-all" /></th>
                <th className="p-2">{t('fileVisibility.fileCol')}</th>
                <th className="p-2">{t('fileVisibility.recordCol')}</th>
                <th className="p-2">{t('fileVisibility.categoryCol')}</th>
                <th className="p-2">{t('fileVisibility.visibilityCol')}</th>
                <th className="p-2">{t('fileVisibility.uploadedCol')}</th>
                <th className="p-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {files.map((x) => (
                <FileRow key={x.id} file={x} checked={selected.includes(x.id)} onSave={saveOne}
                  onCheck={(on) => setSelected((p) => on ? [...p, x.id] : p.filter((i) => i !== x.id))} />
              ))}
              {!files.length && (
                <tr><td colSpan={7} className="p-6 text-center text-gray-500" data-testid="admin-files-empty">{t('fileVisibility.noFiles')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pager page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />
      </CardContent>
    </Card>
  );
};
